import React from 'react'
import { Link } from 'react-router-dom'

import {
  selectDocument,
  selectDocumentFileInfo,
  selectDocumentFileInfoIsLoading,
  selectDocumentLoading,
  selectDocumentSummaryExecutionLoading,
  selectDocumentSummaryPollingEnabled,
  selectDocumentSummaryResult,
  selectDocumentSummaryResultLoading,
  selectSelectedDocumentType,
} from '../../../store/processDocument/document'
import { selectDocumentType } from '../../../store/processDocument/processDocument'
import { useAppSelector } from '../../../store/hooks'

import { DocumentType, FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'

import { Button } from '../../../components/Button/Button'

import { DocumentTypeSelectDropdown } from '../DocumentTypeSelectDropdown/DocumentTypeSelectDropdown'

import { DocumentProcessingProgress } from './DocumentProcessingProgress'
import { DocumentSummaryEditor } from './DocumentSummaryEditor'
import { useDocumentSummary } from './hooks'

import './DocumentSummary.scss'

export const DocumentSummary = () => {
  const document = useAppSelector(selectDocument)
  const documentLoading = useAppSelector(selectDocumentLoading)
  const fileInfoLoading = useAppSelector(selectDocumentFileInfoIsLoading)
  const documentType = useAppSelector(selectDocumentType)

  const summaryResultLoading = useAppSelector(selectDocumentSummaryResultLoading)
  const summaryPollingEnabled = useAppSelector(selectDocumentSummaryPollingEnabled)
  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)

  if (documentLoading || fileInfoLoading) {
    return <div className='skeleton w-100 document-summary-editor-skeleton' />
  }

  if (!document) {
    return null
  }

  if (!document.summaryProcessing.finishedAt) {
    return <DocumentProcessingProgress document={document} />
  }

  if (summaryResultLoading || summaryPollingEnabled || !documentSummaryResult) {
    return <div className='skeleton w-100 document-summary-editor-skeleton' />
  }

  return (
    <>
      <DocumentTypeChangedBadge documentType={documentType} />
      <DocumentSummaryEditor />
    </>
  )
}

interface DocumentTypeChangedBadgeProps {
  documentType: DocumentType | null
}

const DocumentTypeChangedBadge: React.FC<DocumentTypeChangedBadgeProps> = ({ documentType }) => {
  const executionLoading = useAppSelector(selectDocumentSummaryExecutionLoading)
  const selectedDocumentType = useAppSelector(selectSelectedDocumentType)
  const fileInfo = useAppSelector(selectDocumentFileInfo)

  const { handleRerun } = useDocumentSummary()

  if (!documentType || !selectedDocumentType || !fileInfo) {
    return null
  }

  if (documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID) {
    return (
      <div className='d-flex flex-column gap-3 p-3 zocks-bg-color-primary-light rounded-3'>
        <div className='d-flex gap-3'>
          <i className='bi bi-exclamation-circle fs-5' />
          <div className='d-flex flex-column gap-1'>
            <span className='paragraph-medium bold'>Get better results with a specific Document Type</span>
            <span className='paragraph-medium'>
              Your AI Assistant used a generic template because it couldn't find a match. Choose a document type below and rerun, or create a{' '}
              <Link to='/workspace/setup/document-type/document-type/create' target='_blank' className='zocks-text-primary text-decoration-underline'>
                custom Document Type
              </Link>
              .
            </span>
          </div>
        </div>
        <div className='d-flex align-items-center gap-3 zocks-ps-2.25'>
          <div className='document-summary-type-selector-container'>
            <DocumentTypeSelectDropdown />
          </div>
          <Button variant='primary' className='py-2 zocks-px-0.75' disabled={!selectedDocumentType} onClick={handleRerun} loading={executionLoading}>
            Rerun
          </Button>
        </div>
      </div>
    )
  }

  if (documentType.id !== selectedDocumentType.id) {
    return (
      <div className='d-flex align-items-center gap-3 p-3 zocks-bg-color-primary-light rounded-3'>
        <i className='bi bi-exclamation-circle fs-5' />
        <span className='paragraph-medium'>The document type has changed. Rerun processing to apply the new template.</span>
        <Button variant='primary' className='px-2 zocks-py-0.25' onClick={handleRerun} loading={executionLoading}>
          Rerun
        </Button>
      </div>
    )
  }

  return null
}
